import { ApolloError } from '@apollo/client';
import { useMemo } from 'react';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import {
  getAssetStaticIntegratedGroup,
  mapAssetStaticToOption,
  mapPolicyToOption,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/hooks/useEntityAssetOptions';

import { useGetEntityHoldingsForMoveQuery } from '../graphql/GetEntityHoldingsForMove.generated';

export interface UseEntityHoldingsForMoveResult {
  /** Manual asset statics held by the entity (integration-linked ones excluded). */
  assetOptions: TypeaheadSelectInputOption<string>[];
  /** Insurance policies owned by the entity. */
  policyOptions: TypeaheadSelectInputOption<string>[];
  loading: boolean;
  /** Set when the holdings query fails, so callers can distinguish a load error from an empty entity. */
  error?: ApolloError;
}

/**
 * Loads the assets and insurance policies of a single (source) entity for the
 * "Move assets to new owner" side drawer, split into two selectable lists.
 *
 * Only *manual* asset statics are offered: the move mutation rejects
 * integration-linked statics, so we exclude any static that belongs to an
 * integrated/linked holding-account group. Option values are the same
 * `prefix:id` composites the DP asset selector uses, so callers can recover the
 * raw static / policy IDs with `parseAssetOptionValue`.
 */
export function useEntityHoldingsForMove(
  entityId: string | null
): UseEntityHoldingsForMoveResult {
  const { data, loading, error } = useGetEntityHoldingsForMoveQuery({
    variables: { entityId: entityId ?? '' },
    skip: !entityId,
    fetchPolicy: 'cache-and-network',
  });

  return useMemo(() => {
    const node = data?.node;
    const entity = node?.__typename === 'Entity' ? node : null;
    if (!entity) {
      return { assetOptions: [], policyOptions: [], loading, error };
    }

    const holdings = entity.currentHoldings;

    const assetOptions = holdings.assets
      .filter((asset) => getAssetStaticIntegratedGroup(asset) === null)
      .map(mapAssetStaticToOption);

    const policyOptions = holdings.insurancePolicies.map(mapPolicyToOption);

    return { assetOptions, policyOptions, loading, error };
  }, [data, loading, error]);
}
